import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { Formik, Form } from "formik";
import { ReactElement } from "react";
import * as Yup from "yup";

import { AddStaffEmailMutationVariables } from "~frontend/__generated__/graphql";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import { useErrorMessageFormatter } from "~frontend/util/errors";

export default function AddStaffEmailDialog({
  open,
  onClose,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: AddStaffEmailMutationVariables) => Promise<void>;
}): ReactElement {
  const getErrorMessage = useErrorMessageFormatter();
  return (
    <Dialog open={open}>
      <DialogTitle>Add Staff Email</DialogTitle>
      <Formik
        initialValues={{ [submitErrorField]: null, fullName: "", email: "" }}
        validationSchema={Yup.object({
          fullName: Yup.string().required("Required").min(3, "Too short"),
          email: Yup.string()
            .required("Required")
            .min(3, "Too short")
            .email("Must be a valid email")
            // this is just frontend validation; the backend doesn't care
            // but we want to prevent someone accidentally adding a non-STW
            // email because they mix up "staff email" with a client owner email
            .matches(
              /@(contractors\.)?scaletowin\.com$/,
              "Email must be @scaletowin.com or @contractors.scaletowin.com"
            ),
        })}
        onSubmit={async (values, formik) => {
          try {
            await onSubmit({
              input: {
                fullName: values.fullName,
                email: values.email,
              },
            });
            formik.resetForm();
          } catch (e) {
            formik.setErrors({
              [submitErrorField]: getErrorMessage(e),
            });
          }
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogContent sx={{ minWidth: "400px" }}>
              <p>
                This will add a new staff member. This staff member will be able
                to sign in to any client's account -- not just the account
                you're on right now. This should only be used for{" "}
                <strong>Scale to Win Staff Members</strong> who should have
                complete access to all accounts.
              </p>
              <p>
                The new staff member will not receive an invite or notification.
                They can just go to any client account and sign in using their
                shared Auth0 account matching the email you provide here. If
                they don't already have a shared Auth0 account, they can create
                one by signing up for an account on any client account using
                this email address.
              </p>
              <FormikErrorBanner />
              <FormikTextField
                name="email"
                label="Email"
                fullWidth
                inputProps={{ "data-testid": "add-staffemail-email-field" }}
              />
              <FormikTextField
                name="fullName"
                label="Full Name"
                fullWidth
                inputProps={{ "data-testid": "add-staffemail-fullname-field" }}
              />
            </DialogContent>

            <DialogActions>
              <Button
                disabled={isSubmitting}
                onClick={onClose}
                color="secondary"
              >
                Cancel
              </Button>

              <FormikSubmitButton
                color="primary"
                variant="contained"
                submitText="Add"
                loadingText="Adding..."
                data-testid="add-staffemail-button"
              />
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
